// eslint-disable-next-line import/named
import './ui/templateresult/FormInputView';
import { appOutOfDate, getLocalBuildNumber, isDebugMode } from './debug';
import { authenticationOptions } from './api/api-injector';
import { changeLanguage, getDefaultLanguage, globalLanguage, setDefaultLanguage } from '../../localization/global';
import { customElement, property, state } from 'lit/decorators.js';
import { flushAndReloadApplication } from './common/web-cache';
import { footerBrandLogoUrl, isDevSite, isTestSite } from './domain';
import { getAssetCdnPath } from './common/assets';
import { getCurrentUser, setCurrentUser } from './api/current-user';
import { globalCurrencyState } from './currency-formatter';
import { html, LitElement, TemplateResult } from 'lit';
import { ImagePreview, imagePreviewContext } from './context/imagePreview';
import { isCynclyStaff, isSupplierAgent } from './currentuser-claims';
import { isEmptyOrSpace } from './ui/string-helper-functions';
import { IUserSettings } from './context/IUserSettings';
import { LangAppIndex } from '../../localization/webmodule-common/other/app-index-lang';
import {
  localSettingsContext,
  sessionSettingsContext,
  userLocalSettings,
  userSessionSettings
} from './context/UserSettingsContext';
import { provide } from '@lit/context';
import { run2FARegisterV1 } from './ui/loginscreen/authenticate-modal-lit';
import { showDevelopmentError } from './development-error';
import { urlForName } from './router';
import { UserPreferencesModal } from './ui/user-preferences-modal';

export interface MenuItem {
  name: string;
  title: string;
  path: string;
}

@customElement('wm-appindex')
export class AppIndex extends LitElement {
  @property({ type: String }) appTitle?: string;
  @property({ type: String }) currentPath?: string;
  @property({ type: String }) menuItems?: string;
  @property({ type: String }) buildNumber?: string = getLocalBuildNumber();
  @property({ type: String }) userDisplayName?: string;

  @provide({ context: imagePreviewContext })
  imagePreview = new ImagePreview();

  @provide({ context: localSettingsContext })
  userSettings: IUserSettings = userLocalSettings;
  @provide({ context: sessionSettingsContext })
  userSessionSettings: IUserSettings = userSessionSettings;
  @state()
  outOfDate: boolean;
  @state()
  protected logoTemplate: TemplateResult = html` <svg width="1" height="1"></svg>`;
  @state()
  protected supplierResourceLinkTemplate: TemplateResult = html``;

  constructor() {
    super();
    globalThis.dealerConfiguration.litApp = this;
    globalThis.externalLibraryCurrency = globalCurrencyState;

    window.onunhandledrejection = event => {
      console.error(event.reason.stack);

      console.warn(`UNHANDLED PROMISE REJECTION: ${event.reason}`);
      showDevelopmentError(event.reason);
    };

    window.onerror = function (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) {
      //Items to ignore
      if (source?.includes('apexcharts.js')) return;
      if (
        source?.includes('ResizeObserver') ||
        error?.message.includes('ResizeObserver') ||
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        event.toString().includes('ResizeObserver')
      )
        return;

      console.warn(`UNHANDLED ERROR: ${error?.stack}`);
      if (error?.stack) console.error(error.stack);
      showDevelopmentError(
        error ??
          `
    
      ${
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        event
      }
    source:${source}
    line:${lineno}
    col :${colno}
    `
      );
    };
    this.outOfDate = appOutOfDate();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('webmodule-page-navigation', this.updateNavigation);
    this.addEventListener('wm-scroll-into-view', this.scrollElementIntoView);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('webmodule-page-navigation', this.updateNavigation);
    this.removeEventListener('wm-scroll-into-view', this.scrollElementIntoView);
  }

  render() {
    console.log();

    const logoutEvent = async (e: Event) => {
      e.preventDefault();
      await this.disconnectUser();
      await setCurrentUser(null);
      window.location.href = '/login';
    };

    const enable2FAEvent = (e: Event) => {
      e.preventDefault();
      run2FARegisterV1(authenticationOptions());
    };

    const preferencesEvent = (e: Event) => {
      e.preventDefault();
      void new UserPreferencesModal().showModal();
    };
    const languageMenu =
      globalLanguage.languageServer.getLanguages().length > 1
        ? html` <webmodule-divider role="separator" aria-orientation="horizontal" tabindex="-1"></webmodule-divider>
            <webmodule-menu-item> ${this.getCurrentLanguageTemplate()}</webmodule-menu-item> ${this.getLanguageMenu()}`
        : undefined;
    const title2FA = getCurrentUser()?.Is2FAEnabled ? LangAppIndex.registerNew2FAApp() : LangAppIndex.enable2FA();
    // Parked pages (e.g. no available branches) keep only Logout in the Options menu.
    const logoutOnly = this.isLogoutOnlyUserMenu();
    const userMenuItems = logoutOnly
      ? html` <webmodule-menu-item @click=${logoutEvent}>${LangAppIndex.logout()}</webmodule-menu-item> `
      : html`
          ${this.customUserMenuElements()}
          <webmodule-menu-label>${LangAppIndex.user()}</webmodule-menu-label>
          <webmodule-menu-item @click=${preferencesEvent}>${LangAppIndex.preferences()}</webmodule-menu-item>
          <webmodule-menu-item @click=${logoutEvent}>${LangAppIndex.logout()}</webmodule-menu-item>
          <webmodule-menu-item @click=${enable2FAEvent}>${title2FA}</webmodule-menu-item>
          ${languageMenu} ${this.extendedUserMenuItems()}
        `;
    const userMenu = !isEmptyOrSpace(this.userDisplayName)
      ? html` <span class="navbar-text">
          ${this.aboveUserNameTemplate()}

          <webmodule-dropdown placement="bottom">
            <webmodule-button slot="trigger" variant="text" caret>${this.userNameMenuContent()}</webmodule-button>
            <webmodule-menu> ${userMenuItems} </webmodule-menu>
          </webmodule-dropdown>
        </span>`
      : html``;
    const supportClasses = isCynclyStaff()
      ? 'global-support-section cyncly-staff'
      : isSupplierAgent()
        ? 'global-support-section supplier'
        : '';
    const supplierOnlineTemplate = this.serviceOnlineTemplate();
    //this code is based on expecting only a single supplier instance, not multi instance
    const supportTemplate = isEmptyOrSpace(supportClasses)
      ? html``
      : html` <div class=${supportClasses}>${this.globalSupportInformation()}</div>`;
    return html`
      ${supplierOnlineTemplate} ${supportTemplate}
      <header>
        <nav class="navbar navbar-expand-lg navbar-header">
          <div class="container-fluid px-3">
            <a class="navbar-brand" href="/"> ${this.logoTemplate} </a>
            <button
              class="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target=".navbar-collapse"
              aria-controls="navbarSupportedContent"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span class="navbar-toggler-icon"></span>
            </button>
            <div class="navbar-collapse collapse" id="siteNavBar">
              <ul class="navbar-nav me-auto mb-2 mb-lg-0">
                ${this.getMenuItems().map(x => this.getMenuItemTemplate(x))}
              </ul>
              <wm-webmoduleautosaveindicator></wm-webmoduleautosaveindicator>
              <span class="me-1"></span>
              <icon-notification-signal></icon-notification-signal>
              ${userMenu}
            </div>
          </div>
        </nav>
      </header>

      <main id="main-page" role="main" class="flex-shrink-0 main-page">
        <div id="main-body"></div>
      </main>

      <footer class="footer mt-auto">
        <div class="d-flex flex-wrap justify-content-center align-content-center justify-content-lg-between">
          <div
            class="d-flex flex-wrap flex-lg-nowrap justify-content-center justify-content-md-start align-items-center footer-left-col"
          >
            <div class="st-logo pe-3">
              <img src=${getAssetCdnPath('./assets/images/PoweredBySoftTech.svg')} alt="Powered by SoftTech online" />
            </div>
            <div class="st-copyright pe-3">&copy; SoftTech (NZ) Ltd ${new Date().getFullYear()}</div>
            <div class="st-build px-3 border-start border-white">${this.generateBuildNumberText()}</div>
          </div>
          <div
            class="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center footer-right-col"
          >
            <div class="supplier-footer-logo">${this.supplierFooterTemplate()}</div>
          </div>
        </div>
      </footer>
    `;
  }

  serviceOnlineTemplate() {
    return html``;
  }

  generateBuildNumberText(): TemplateResult {
    return html`${LangAppIndex.build(this.buildNumber ?? '')}`;
  }

  supplierFooterTemplate(): TemplateResult {
    const debugTemplate = isDebugMode()
      ? html` <webmodule-button size="small" variant="danger" @click=${() => flushAndReloadApplication()}
          >Hot Reload
        </webmodule-button>`
      : html``;

    return isTestSite()
      ? html`${this.supplierResourceLinkTemplate}
          <h1>${LangAppIndex.testEnvironment()} ${debugTemplate}</h1>`
      : isDevSite()
        ? html`<h1>${LangAppIndex.devEnvironment()}${debugTemplate}</h1>`
        : html`${this.supplierResourceLinkTemplate}<img
              src="${footerBrandLogoUrl()}"
              alt="Supplier Branded logo"
            />${debugTemplate}`;
  }

  globalSupportInformation(): unknown {
    return '';
  }

  userNameMenuContent(): unknown {
    return html` ${LangAppIndex.welcome(this.userDisplayName ?? '')}`;
  }

  aboveUserNameTemplate(): unknown {
    return html``;
  }

  customUserMenuElements(): TemplateResult {
    return html``;
  }

  async disconnectUser() {
    //do nothing
    return true;
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  protected getMenuItems(): MenuItem[] {
    if (this.menuItems) return JSON.parse(this.menuItems);
    return [];
  }

  protected getMenuItemTemplate(menuItem: MenuItem): TemplateResult {
    return html` <li class=${'nav-item ' + this.isPathActive(menuItem)}>
      <a class="nav-link" aria-current="page" href="${urlForName(menuItem.name)}">${menuItem.title}</a>
    </li>`;
  }

  protected updateLogoTemplate() {
    this.logoTemplate = html` <svg width="1" height="1"></svg>`;
  }

  private isPathActive(menuItem: MenuItem): string {
    const currentPath = this.currentPath;

    return currentPath?.toLowerCase() === menuItem.path.toLowerCase() ||
      (menuItem.path.toLowerCase() !== '/' && currentPath?.toLowerCase().startsWith(menuItem.path.toLowerCase()))
      ? ' active'
      : '';
  }

  private updateNavigation = e => {
    this.currentPath = e.detail.path;
  };

  private scrollElementIntoView = (evt: Event) => {
    const e = evt as CustomEvent<{
      element?: Element | undefined;
      id?: string | undefined;
      selector?: string | undefined;
    }>;
    let element: Element | undefined = e.detail.element;

    if (!element && e.detail.id) {
      element = this.deepQuerySelector(`#${e.detail.id}`);
    }

    if (!element && e.detail.selector) {
      element = this.deepQuerySelector(e.detail.selector);
    }

    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  /**
   * querySelector that also descends into open shadow roots. Scroll targets
   * like the table rows (div[data-rowid]) render inside component shadow DOM,
   * where a light-DOM lookup from the app root cannot see them.
   */
  private deepQuerySelector(selector: string, root: ParentNode = this): Element | undefined {
    const direct = root.querySelector(selector);
    if (direct) return direct;
    for (const el of root.querySelectorAll('*')) {
      if (el.shadowRoot) {
        const found = this.deepQuerySelector(selector, el.shadowRoot);
        if (found) return found;
      }
    }
    return undefined;
  }

  protected extendedUserMenuItems(): TemplateResult | undefined {
    return undefined;
  }

  /**
   * When true, the top-right Options menu shows only Logout.
   * Default off for all portals; franchisee overrides for the
   * no-available-branches parked state.
   */
  protected isLogoutOnlyUserMenu(): boolean {
    return false;
  }

  private getCurrentLanguageTemplate() {
    const label = this.getDisplayValueForLanguage(globalLanguage.languageProvider.currentLanguage);
    return html` <webmodule-icon
        library="fa"
        name="fas-language"
        class="fa-3x v-center"
        @click=${this.switchLangDebugMode}
      ></webmodule-icon
      ><span class="ms-2">${label}</span>`;
  }

  private getDisplayValueForLanguage(language: string): string {
    switch (language) {
      case 'en':
        return 'English';
      case 'fr':
        return 'Français';
      case 'de':
        return 'Deutsch';
      case 'es':
        return 'Español';
      case 'it':
        return 'Italiano';
      case 'nl':
        return 'Nederlands';
      case 'pt':
        return 'Português';
      default:
        return language;
    }
  }

  private switchLangDebugMode = () => {
    globalThis.languageServerDebugMode(!globalThis.globalLanguageDebugMode);
  };

  private getLanguageMenu() {
    const changeLangEvent = (lang: string) => {
      return async () => {
        changeLanguage(lang);
      };
    };

    const makeDefaultLangEvent = (e: Event) => {
      e.preventDefault();
      const defaultLang = globalLanguage.languageProvider.currentLanguage;
      setDefaultLanguage(defaultLang);
    };

    const allowedLanguages = globalThis.dealerConfiguration.languages as string[];
    const availableLanguages = globalLanguage.languageServer.getLanguages();
    const currentLanguage = globalLanguage.languageProvider.currentLanguage;
    const filteredLanguages = allowedLanguages.filter(
      lang => availableLanguages.includes(lang) && lang !== currentLanguage
    );
    const defaultLanguage = getDefaultLanguage();
    const currentLangIsDefault = currentLanguage === defaultLanguage;
    return html`${filteredLanguages.map(
        lang =>
          html` <webmodule-menu-item ?disabled=${lang === currentLanguage} @click=${changeLangEvent(lang)}>
            <span class="ms-5"> ${this.getDisplayValueForLanguage(lang)}</span>
          </webmodule-menu-item>`
      )}
      <webmodule-divider></webmodule-divider>
      <webmodule-menu-item ?disabled=${currentLangIsDefault} @click=${makeDefaultLangEvent}>
        ${LangAppIndex.setAsDefault(this.getDisplayValueForLanguage(currentLanguage))}
      </webmodule-menu-item> `;
  }
}
